import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, Star, Save, Check, Info } from "lucide-react";

export const PresetsDialog = ({
  open,
  onOpenChange,
  presets,
  activePresetId,
  onApply,
  onSaveCurrent,
  onDelete,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="presets-dialog"
        className="max-w-lg bg-[#0f1015] border-zinc-800"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-100">Palette Presets</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Load a saved palette or save your current one for later. Hover the info icon to see each preset&apos;s description.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Available presets
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto scroll-thin pr-1">
              {presets.map((p) => {
                const isLoaded = p.id === activePresetId;
                const hasDescription = !!(p.description && p.description.trim());
                return (
                  <div
                    key={p.id}
                    data-testid={`preset-${p.id}`}
                    className={`flex items-center gap-3 rounded-lg border p-2.5 transition ${
                      isLoaded
                        ? "border-rose-500/40 bg-rose-500/5"
                        : "border-zinc-800 bg-[#131418]"
                    }`}
                  >
                    <div className="flex gap-0.5 h-8 flex-1 rounded overflow-hidden">
                      {p.layers.slice(0, 12).map((l, i) => (
                        <div
                          key={i}
                          className="flex-1"
                          style={{ backgroundColor: l.color }}
                        />
                      ))}
                    </div>
                    <div className="flex flex-col items-start min-w-[130px]">
                      <span className="text-sm text-zinc-100 font-medium flex items-center gap-1.5">
                        {p.builtIn && (
                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        )}
                        {p.name}
                        {hasDescription && (
                          <TooltipProvider delayDuration={80}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`preset-info-${p.id}`}
                                  className="text-zinc-500 hover:text-rose-400 cursor-help transition"
                                >
                                  <Info className="w-3 h-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs bg-[#141519] border-zinc-700 text-zinc-100"
                              >
                                <div
                                  data-testid={`preset-description-${p.id}`}
                                  className="text-xs leading-relaxed"
                                >
                                  {p.description}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                      <span className="text-[10px] text-zinc-500">
                        {p.layers.length} layers
                      </span>
                    </div>
                    <Button
                      data-testid={`apply-preset-${p.id}`}
                      size="sm"
                      disabled={isLoaded}
                      onClick={() => {
                        onApply(p);
                        onOpenChange(false);
                        toast.success(`Loaded "${p.name}"`);
                      }}
                      className={
                        isLoaded
                          ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/15 cursor-default h-8 border border-emerald-500/30"
                          : "bg-rose-500 hover:bg-rose-400 text-white h-8"
                      }
                    >
                      {isLoaded ? (
                        <>
                          <Check className="w-3.5 h-3.5 mr-1" /> Loaded
                        </>
                      ) : (
                        "Load"
                      )}
                    </Button>
                    {!p.builtIn && (
                      <button
                        data-testid={`delete-preset-${p.id}`}
                        onClick={() => {
                          onDelete(p.id);
                          toast.info(`Deleted "${p.name}"`);
                        }}
                        className="p-1.5 text-zinc-500 hover:text-rose-400 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-800 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Save current palette
            </div>
            <Input
              data-testid="preset-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Preset name"
              className="bg-[#131418] border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
            />
            <textarea
              data-testid="preset-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional) — shown on hover in the presets list"
              rows={2}
              className="w-full bg-[#131418] border border-zinc-800 rounded-md text-zinc-100 placeholder:text-zinc-600 text-sm px-3 py-2 resize-none focus:outline-none focus:border-rose-500/50 transition"
            />
            <Button
              data-testid="save-preset-btn"
              onClick={() => {
                if (!name.trim()) {
                  toast.error("Give your preset a name.");
                  return;
                }
                onSaveCurrent(name.trim(), description.trim());
                setName("");
                setDescription("");
                toast.success("Preset saved");
              }}
              className="w-full bg-rose-500 hover:bg-rose-400 text-white"
            >
              <Save className="w-4 h-4 mr-1.5" />
              Save preset
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
